/**
 * Two side-by-side breakdown cards for the Space Overview.
 *
 *   Left  — "Tasks by status": one segmented horizontal bar whose
 *           segments correspond to the workflow's actual statuses
 *           (name + colour from the `tasks.byStatus` API field). A
 *           legend below the bar lists each status with its count so
 *           a tiny segment is still readable.
 *
 *   Right — "Open tasks by priority": the same PriorityRow pattern
 *           the legacy List-scoped Overview shipped, fed by the API's
 *           pre-computed `tasks.byPriority` block.
 *
 * Both cards degrade to an "All clear" empty message when their
 * input is empty rather than rendering an empty bar.
 */

import { Flag } from 'lucide-react'

const PRIORITY_META = {
  urgent: { label: 'Urgent', color: 'var(--color-priority-urgent)' },
  high: { label: 'High', color: 'var(--color-priority-high)' },
  normal: { label: 'Medium', color: 'var(--color-priority-normal)' },
  low: { label: 'Low', color: 'var(--color-priority-low)' },
}

export function SpaceOverviewBreakdowns({ overview }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <StatusBreakdown rows={overview.tasks.byStatus} total={overview.tasks.total} />
      <PriorityBreakdown
        counts={overview.tasks.byPriority}
        openTotal={overview.tasks.open}
      />
    </div>
  )
}

function StatusBreakdown({ rows, total }) {
  const nonZero = (rows ?? []).filter((r) => r.count > 0)
  const safeTotal = total || nonZero.reduce((acc, r) => acc + r.count, 0) || 1

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <p className="font-display text-lg font-medium tracking-tight text-[color:var(--color-ink-strong)] mb-3">
        Tasks by status
      </p>

      {nonZero.length === 0 ? (
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          No tasks tracked in this Space yet.
        </p>
      ) : (
        <>
          <div
            className="flex h-3 w-full rounded-full overflow-hidden bg-[color:var(--color-canvas)]"
            role="img"
            aria-label="Task status distribution"
          >
            {nonZero.map((r) => (
              <span
                key={r.name}
                title={`${r.name}: ${r.count}`}
                style={{
                  width: `${(r.count / safeTotal) * 100}%`,
                  backgroundColor: r.color,
                }}
              />
            ))}
          </div>
          <div className="mt-3 space-y-2">
            {(rows ?? []).map((r) => (
              <div
                key={r.name}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: r.color }}
                  />
                  <span className="truncate text-[color:var(--color-ink)]">
                    {r.name}
                  </span>
                </span>
                <span className="tabular-nums text-[color:var(--color-ink-muted)]">
                  {r.count}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function PriorityBreakdown({ counts, openTotal }) {
  const safeTotal = openTotal || 1
  const order = ['urgent', 'high', 'normal', 'low']
  const hasAnyOpen =
    Object.values(counts ?? {}).reduce((acc, n) => acc + (n || 0), 0) > 0

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <p className="font-display text-lg font-medium tracking-tight text-[color:var(--color-ink-strong)] mb-3">
        Open tasks by priority
      </p>
      {!hasAnyOpen ? (
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          Nothing open — every task is closed or this Space has no tasks yet.
        </p>
      ) : (
        <div className="space-y-2">
          {order.map((p) => (
            <PriorityRow
              key={p}
              label={PRIORITY_META[p].label}
              color={PRIORITY_META[p].color}
              count={counts?.[p] ?? 0}
              total={safeTotal}
            />
          ))}
          <PriorityRow
            label="No priority"
            color="var(--color-ink-subtle)"
            count={counts?.none ?? 0}
            total={safeTotal}
          />
        </div>
      )}
    </div>
  )
}

function PriorityRow({ label, color, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="inline-flex items-center gap-1.5 text-sm">
          <Flag size={13} fill={color} strokeWidth={0} />
          <span style={{ color }}>{label}</span>
        </span>
        <span className="text-sm tabular-nums text-[color:var(--color-ink-muted)]">
          {count}
        </span>
      </div>
      <div className="h-1.5 bg-[color:var(--color-rule)] rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}
